import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
    useSyncExternalStore,
    type ReactNode,
} from 'react';
import { onSessionExpired } from '@/lib/auth/sessionEvents';
import { clearSignedInStorage } from '@/lib/auth/signedInStorage';
import {
    clearAccessToken,
    getAccessToken,
    onAccessTokenChange,
    setAccessToken,
} from '@/lib/auth/tokenStore';

/**
 * Why the session ended. `session-expired` shows the sign-in notice;
 * `user-signed-out` (the header's Sign out, 846) makes `RequireSession` redirect
 * to a bare /login with no `next`.
 */
export type SignOutReason = 'session-expired' | 'user-signed-out';

export interface SessionValue {
    status: 'signed-in' | 'signed-out';
    accessToken: string | null;
    signOutReason: SignOutReason | null;
    signIn(token: string): void;
    signOut(reason?: SignOutReason): void;
}

const SessionContext = createContext<SessionValue | null>(null);

export function useSession(): SessionValue {
    const value = useContext(SessionContext);
    if (!value) {
        throw new Error('useSession must be used inside <SessionProvider>');
    }
    return value;
}

interface SessionProviderProps {
    children: ReactNode;
}

/**
 * The authenticated session, derived from the existing `tokenStore` rather
 * than holding its own copy (state.md: "server data is not component
 * state"). `onAccessTokenChange` already returns an unsubscribe function,
 * which is exactly `useSyncExternalStore`'s subscribe contract, and
 * `getAccessToken` returns a primitive, so there is no new-object-every-
 * render loop.
 *
 * The token itself is persisted by `tokenStore` in `sessionStorage` (per
 * tab, ticket 825), so a reload stays signed in.
 *
 * `signOutReason` is the one piece of state this provider actually owns — it
 * is not server data, and it is intentionally memory-only: a reload loses it,
 * which is what makes a reload show a clean sign-in screen with no error
 * copy.
 *
 * An expired session reported by the API (see `@/lib/apollo/client`) arrives
 * as a `sessionEvents` notice and is turned into a `signOut` here.
 */
export function SessionProvider({ children }: Readonly<SessionProviderProps>) {
    const token = useSyncExternalStore(onAccessTokenChange, getAccessToken);
    const [signOutReason, setSignOutReason] = useState<SignOutReason | null>(
        null
    );

    // signIn/signOut: stable because `signOut` is an effect dependency and
    // both sit inside the memoised context value below.
    const signIn = useCallback((accessToken: string) => {
        setAccessToken(accessToken);
        setSignOutReason(null);
    }, []);

    const signOut = useCallback((reason?: SignOutReason) => {
        // An expired session keeps the tab's signed-in data: the same admin
        // usually signs straight back in. Any other sign-out clears it.
        if (reason !== 'session-expired') clearSignedInStorage();
        clearAccessToken();
        setSignOutReason(reason ?? null);
    }, []);

    // Subscribes to a source outside React and cleans up after itself.
    useEffect(
        () =>
            onSessionExpired(() => {
                if (getAccessToken()) {
                    signOut('session-expired');
                }
            }),
        [signOut]
    );

    // Context value: a new identity each render would re-render every consumer.
    const value = useMemo<SessionValue>(
        () => ({
            status: token ? 'signed-in' : 'signed-out',
            accessToken: token,
            signOutReason,
            signIn,
            signOut,
        }),
        [token, signOutReason, signIn, signOut]
    );

    return (
        <SessionContext.Provider value={value}>
            {children}
        </SessionContext.Provider>
    );
}
